import React, { useContext } from "react";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { ThemeProvider } from "styled-components";
import DocumentTitle from "react-document-title";
import { SignatureComponent } from "@/components/Signature/SignatureComponent";
import { useKycSignatureController } from "@/screen/Identities/KycSignature/useKycSignatureController";
import { HelperStyle, Cont, MainCont, Title, BarCont, BgBar, FormContainer, ButtonsRow, ServerWarningBanner, KycBlockingContainer, ContractHtmlContainer } from "@/screen/Identities/KycSignature/Styles/KycSignatureStyles";

const KycSignatureScreen: React.FC = () =>
{
    const themeContext = useContext(ThemeContext);

    const
    {
        userIdentityName,
        phase,
        isLoading,
        loadError,
        kycBlockingReasons,
        documentUrl,
        signatures,
        obfuscatedSignPhone,
        handleProceedToSignature,
        handleBackToContract,
        handleCancel,
        handleGoToEdit,
        handleSigned,
        handleSignaturesChanged,
    } = useKycSignatureController();

    const title = "Firma del KYC";
    const subtitle = userIdentityName ? `Interviniente: ${userIdentityName}` : "Firma del cuestionario de conocimiento del cliente.";

    return(
        <DocumentTitle title={`IronIA - ${title}`}>
            <React.Fragment>
                <HelperStyle />
                <Cont theme={themeContext} className="kyc-signature">
                    <ThemeProvider theme={themeContext}>
                        <MainCont>
                            <Title>
                                <h1>{title}</h1>
                                <h2>{subtitle}</h2>
                            </Title>
                            <BarCont>
                                <BgBar />
                            </BarCont>
                            {isLoading ?
                            (
                                <FormContainer theme={themeContext}>
                                    <div style={{textAlign: "center", padding: "40px"}}>
                                        <Spinner size={SpinnerSize.large} />
                                    </div>
                                </FormContainer>
                            ) :
                            kycBlockingReasons ?
                            (
                                <FormContainer theme={themeContext}>
                                    {/* El KYC no puede firmarse hasta que los datos del interviniente estén completos:
                                        se enumeran todos los motivos que devuelve la validación, no solo el primero. */}
                                    <KycBlockingContainer role="alert">
                                        <h3>No se puede realizar la firma del KYC porque los datos del interviniente necesarios no están cumplimentados.</h3>
                                        <ul>
                                            {kycBlockingReasons.map((reason, index) =>
                                            (
                                                <li key={index}>{reason}</li>
                                            ))}
                                        </ul>
                                    </KycBlockingContainer>
                                    <ButtonsRow>
                                        <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                            Volver
                                        </DefaultButton>
                                        <DefaultButton className="button tiny-primary-button" onClick={handleGoToEdit}>
                                            Ir a la edición del interviniente
                                        </DefaultButton>
                                    </ButtonsRow>
                                </FormContainer>
                            ) :
                            loadError ?
                            (
                                <FormContainer theme={themeContext}>
                                    <ServerWarningBanner role="alert">{loadError}</ServerWarningBanner>
                                    <ButtonsRow>
                                        <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                            Volver
                                        </DefaultButton>
                                    </ButtonsRow>
                                </FormContainer>
                            ) :
                            phase === "contract" ?
                            (
                                <FormContainer theme={themeContext}>
                                    {/* El documento KYC (PDF) se muestra embebido; el interviniente lo lee antes de firmar.
                                        El iframe SOLO se monta si hay documento: un src vacío hace que el navegador cargue
                                        la propia URL de la página dentro del iframe (la aplicación se re-arranca anidada y
                                        vuelve a lanzar la generación del KYC en bucle). */}
                                    <ContractHtmlContainer theme={themeContext}>
                                        {documentUrl &&
                                        (
                                            <iframe
                                                title="Documento KYC"
                                                src={documentUrl}
                                                style={{width: "100%", height: "70vh", border: "none"}}
                                            />
                                        )}
                                    </ContractHtmlContainer>
                                    <ButtonsRow>
                                        <DefaultButton className="button tiny-secondary-button" onClick={handleCancel}>
                                            Cancelar
                                        </DefaultButton>
                                        <DefaultButton className="button tiny-primary-button" onClick={handleProceedToSignature} disabled={!documentUrl}>
                                            Firmar KYC
                                        </DefaultButton>
                                    </ButtonsRow>
                                </FormContainer>
                            ) :
                            (
                                <FormContainer theme={themeContext}>
                                    {/* OTP reutilizando el componente compartido; al verificar se finaliza en backend. */}
                                    <SignatureComponent
                                        signatures={signatures}
                                        title="Firma del KYC"
                                        submitButtonText="Firmar KYC"
                                        cancelButtonText="Volver"
                                        backButtonText="Volver"
                                        alreadySignedText="El KYC se ha firmado correctamente."
                                        headerTextArea={obfuscatedSignPhone
                                            ? `Te hemos enviado un código SMS al teléfono ${obfuscatedSignPhone} para firmar tu cuestionario de conocimiento del cliente.`
                                            : "Te hemos enviado un código SMS para firmar tu cuestionario de conocimiento del cliente."}
                                        allowRequestNewCode
                                        requestNewCodeButtonText="Recibir nuevo código"
                                        onBackButtonClicked={handleBackToContract}
                                        onCancelButtonClicked={handleBackToContract}
                                        onSigned={handleSigned}
                                        onNewCodeSended={handleSignaturesChanged}
                                        onSignatureChanged={handleSignaturesChanged}
                                    />
                                </FormContainer>
                            )}
                        </MainCont>
                    </ThemeProvider>
                </Cont>
            </React.Fragment>
        </DocumentTitle>
    );
};

export default KycSignatureScreen;
